import React, { useMemo, useState } from "react";
import { Modal } from "react-responsive-modal";
import "react-responsive-modal/styles.css";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faChevronLeft,
  faChevronRight,
  faTimes,
} from "@fortawesome/free-solid-svg-icons";

export default function ViewQuoteProduct({
  open,
  onClose,
  Field,
  selectedProductOption,
  selectedProductImages,
  handleAddItem,
}) {
  const productName = selectedProductOption?.value?.name || "Untitled product";
  const [isImageModalOpen, setIsImageModalOpen] = useState(false);
  const [modalStartIndex, setModalStartIndex] = useState(0);

  const getProductImageUrl = (img) => {
    if (!img) return "";
    if (typeof img === "string") return img;
    return img.src || img.image_url || img.url || img.thumbnail || "";
  };

  const normalizedImages = useMemo(
    () => (selectedProductImages || []).map(getProductImageUrl).filter(Boolean),
    [selectedProductImages],
  );

  const openImageModal = (startIndex = 0) => {
    setModalStartIndex(Number.isFinite(startIndex) ? startIndex : 0);
    setIsImageModalOpen(true);
  };

  const closeImageModal = () => {
    setIsImageModalOpen(false);
    setModalStartIndex(0);
  };

  return (
    <>
      {/* Product Details Modal */}
      <Modal
        open={open}
        onClose={onClose}
        center
        showCloseIcon={false}
        classNames={{ modal: "quote-product-modal create-quote-page pt-0" }}
      >
        <div className="quote-form">
          <div className="quote-product-modal-header">
            <h3>Product Details</h3>
            <button
              type="button"
              className="quote-product-modal-close-btn"
              onClick={onClose}
              aria-label="Close product details modal"
            >
              <FontAwesomeIcon icon={faTimes} />
            </button>
          </div>

          <label className="field-label" htmlFor="productName">
            Product Name
          </label>
          <Field
            id="productName"
            name="productName"
            type="text"
            placeholder="Enter product name"
            className="form-control"
            disabled={!!selectedProductOption}
            readOnly={!!selectedProductOption}
          />

          <label className="field-label" htmlFor="description">
            Description
          </label>
          <Field
            id="description"
            name="description"
            as="textarea"
            placeholder="Enter product description"
            rows={3}
            className="form-control"
            disabled={!!selectedProductOption}
            readOnly={!!selectedProductOption}
          />

          {normalizedImages.length > 0 ? (
            <div
              className="product-images-section"
              style={{
                padding: 8,
                borderRadius: 4,
                marginTop: 8,
                background: "#fff",
              }}
            >
              <div
                className="image-thumbs"
                style={{
                  display: "flex",
                  gap: 16,
                  alignItems: "center",
                  flexWrap: "wrap",
                }}
              >
                {normalizedImages.map((img, idx) => {
                  return (
                    <img
                      key={idx}
                      src={img}
                      alt={`${productName}-${idx + 1}`}
                      className="product-thumb"
                      style={{
                        cursor: "pointer",
                        width: 120,
                        height: 120,
                        objectFit: "cover",
                        border: "2px solid #000",
                        borderRadius: 4,
                      }}
                      onClick={() => openImageModal(idx)}
                    />
                  );
                })}
              </div>
            </div>
          ) : (
            <div className="quote-product-no-image">No images available</div>
          )}

          <div className="form-row">
            <div>
              <label className="field-label" htmlFor="asin">
                ASIN
              </label>
              <Field
                id="asin"
                name="asin"
                type="text"
                placeholder="Enter ASIN"
                className="form-control"
                disabled={!!selectedProductOption}
                readOnly={!!selectedProductOption}
              />
            </div>
            <div>
              <label className="field-label" htmlFor="quantity">
                Quantity
              </label>
              <Field
                id="quantity"
                name="quantity"
                type="number"
                min={1}
                className="form-control"
              />
            </div>
          </div>

          <label className="field-label" htmlFor="notes">
            Notes
          </label>
          <Field
            id="notes"
            name="notes"
            as="textarea"
            placeholder="Size, color, etc."
            rows={2}
            className="form-control"
          />

          <div className="form-actions quote-product-modal-footer">
            <button
              type="button"
              className="btn btn-add"
              onClick={handleAddItem}
            >
              Add to Quote
            </button>
          </div>
        </div>
      </Modal>

      {/* Product Images Modal */}
      <Modal
        open={isImageModalOpen}
        onClose={closeImageModal}
        center
        classNames={{ modal: "quote-image-modal" }}
      >
        <div className="quote-modal-slider">
          {normalizedImages.length > 0 ? (
            (() => {
              const currentSrc = normalizedImages[modalStartIndex];
              const prev = () =>
                setModalStartIndex((i) => Math.max(0, (i || 0) - 1));
              const next = () =>
                setModalStartIndex((i) =>
                  Math.min(normalizedImages.length - 1, (i || 0) + 1),
                );

              return (
                <div className="quote-modal-inner">
                  <button
                    type="button"
                    onClick={prev}
                    disabled={(modalStartIndex || 0) <= 0}
                    className="quote-modal-prev"
                  >
                    <FontAwesomeIcon icon={faChevronLeft} />
                  </button>

                  <div className="quote-modal-image-wrapper">
                    {currentSrc ? (
                      <img
                        src={currentSrc}
                        alt={`image-${modalStartIndex}`}
                        className="quote-modal-image"
                      />
                    ) : (
                      <div className="quote-modal-empty">
                        No image available
                      </div>
                    )}
                  </div>

                  <button
                    type="button"
                    onClick={next}
                    disabled={
                      (modalStartIndex || 0) >= normalizedImages.length - 1
                    }
                    className="quote-modal-next"
                  >
                    <FontAwesomeIcon icon={faChevronRight} />
                  </button>
                </div>
              );
            })()
          ) : (
            <div className="quote-modal-empty">No images available</div>
          )}
        </div>
      </Modal>
    </>
  );
}
